/**
 * Rolurile și ce au voie — SURSA UNICĂ.
 *
 * Motivul pentru care fișierul ăsta există: gărzile de rol erau împrăștiate și scrise
 * ca DENY-LIST (`role !== "OPERATOR"` = „oricine altcineva"). Tiparul ăsta e sigur cât
 * timp lista de roluri nu crește — la primul rol nou, fiecare astfel de gardă îl lasă
 * să treacă TĂCUT. Adăugarea rolului HR a scos la iveală exact asta: fără fișierul de
 * față, HR ar fi moștenit dreptul de a aproba pontaje și de a intra în trezorerie.
 *
 * Regula: gărzile se scriu ca ALLOW-LIST, într-un singur loc, iar necunoscutul cade pe
 * refuz. Fișierul nu importă „server-only": e folosit și de componente client, care
 * decid ce butoane arată (securitatea reală rămâne pe server).
 */

import { ACTION_MODULES } from "@/lib/actions/types";

/** Rolurile umane, cele din `UserRole` (Prisma). */
export const COMPANY_ROLES = ["ADMIN", "HR", "MANAGER", "OPERATOR"] as const;
export type CompanyRole = (typeof COMPANY_ROLES)[number];

/** Rolurile ne-umane, care nu se atribuie niciunui utilizator. */
export type SystemRole = "SYSTEM" | "PLATFORM";
export type AnyRole = CompanyRole | SystemRole;

export const isCompanyRole = (v: unknown): v is CompanyRole =>
  typeof v === "string" && (COMPANY_ROLES as readonly string[]).includes(v);

export const isAnyRole = (v: unknown): v is AnyRole =>
  isCompanyRole(v) || v === "SYSTEM" || v === "PLATFORM";

export const ROLE_LABEL: Record<CompanyRole, string> = {
  ADMIN: "Administrator",
  HR: "Resurse umane",
  MANAGER: "Manager",
  OPERATOR: "Operator",
};

/** Eticheta unui rol venit ca string (din DB sau din sesiune), fără să crape la necunoscut. */
export const roleLabel = (role: string | undefined | null): string =>
  isCompanyRole(role) ? ROLE_LABEL[role] : (role ?? "");

export const ROLE_HINT: Record<CompanyRole, string> = {
  ADMIN: "toate modulele, plus utilizatori, integrări și date de personal",
  HR: "dosare de personal, contracte, concedii — fără vânzări, facturi sau bani",
  MANAGER: "toate modulele operaționale",
  OPERATOR: "operațional, fără Trezorerie și Integrări",
};

/** Descrierea unui rol venit ca string, fără să crape la necunoscut. */
export const roleHint = (role: string | undefined | null): string =>
  isCompanyRole(role) ? ROLE_HINT[role] : "";

/**
 * Politica de acces pe module, per rol.
 * `"*"` = toate modulele. `writeDenied` scoate module din `"*"`.
 *
 * ADMIN / MANAGER / OPERATOR păstrează EXACT comportamentul dinainte — schimbarea aici
 * a fost gândită să nu miște nimic pentru rolurile existente, doar să facă politica
 * explicită și să închidă necunoscutul.
 */
export type RolePolicy = { write: "*" | readonly string[]; read: "*" | readonly string[]; writeDenied?: readonly string[] };

export const ROLE_POLICY: Record<CompanyRole, RolePolicy> = {
  ADMIN: { write: "*", read: "*" },
  /** Managerul conduce operațiunea; bariera lui rămâne `adminOnly`. */
  MANAGER: { write: "*", read: "*" },
  /** Operatorul: tot, mai puțin banii și integrările (regula dinainte). */
  OPERATOR: { write: "*", read: "*", writeDenied: ["trezorerie", "integrari"] },
  /**
   * HR e DELIBERAT îngust: dosare, documente și comunicare. Vede proiectele doar ca să
   * poată citi prezența, și nu atinge deloc vânzări, facturi, stocuri sau trezorerie —
   * ăsta e chiar motivul pentru care rolul există separat de ADMIN.
   */
  HR: {
    write: ["hr", "documente", "comunicare"],
    read: ["hr", "documente", "comunicare", "proiecte", "sistem", "rapoarte"],
  },
};

/** Are rolul INCORPORAT dreptul cerut pe modul? Necunoscutul cade pe refuz. */
export function policyAllows(role: CompanyRole, module: string, mutates: boolean): boolean {
  const p = ROLE_POLICY[role];
  if (!p) return false;
  if (mutates) {
    if (p.writeDenied?.includes(module)) return false;
    return p.write === "*" || p.write.includes(module);
  }
  if (p.read === "*") return true;
  if (p.read.includes(module)) return true;
  // dreptul de scriere include implicit citirea
  return p.write === "*" || p.write.includes(module);
}

/* ══ PERMISIUNI ═══════════════════════════════════════════════════════════════════════
   Moneda de acces a aplicației nu mai e numele rolului, ci SETUL DE PERMISIUNI. Rolurile
   incorporate își produc setul din `ROLE_POLICY` de mai sus (deci regula rămâne scrisă
   într-un singur loc), iar rolurile configurate de client îl produc din rândurile lor.

   De ce a trebuit schimbată moneda: gărzile erau comparații pe șirul rolului
   (`role === "ADMIN" || role === "HR"`). Cu roluri definite de client, o astfel de gardă
   nu poate răspunde „da" NICIODATĂ, oricâte drepturi i-ai bifa rolului — și ar face
   funcția „compune-ți rolurile" o promisiune goală exact la drepturile sensibile. */

/**
 * Nivelurile pe modul, crescător. `VIEW` („Vizualizare”) = omul NU vede modulul (meniu,
 * pagini, liste), dar îi poate căuta și alege înregistrările din documentele modulelor la
 * care are acces — prin acțiunile marcate `lookup` (ex. clientul pe factură).
 */
export type PermLevel = "NONE" | "VIEW" | "READ" | "WRITE";

export type Caps = {
  admin: boolean;
  approveTime: boolean;
  viewOthersTimesheet: boolean;
  manageHr: boolean;
  seeHrSensitive: boolean;
  approveLeave: boolean;
  seeAllRecords: boolean;
  /** Aprobă facturi de furnizor înainte de plată. */
  approvePayments: boolean;
  /** Aprobă deal-uri și oferte peste pragurile din `ApprovalRule` (discount/valoare). */
  approveDeals: boolean;
  /**
   * Administrează FIRMELE instalării: le vede pe toate, creează firme noi, dă și scoate accesul
   * oamenilor la ele.
   *
   * ⚠ Singura capabilitate care trece GRANIȚA de firmă. Toate celelalte se opresc la firma
   * curentă; asta e, prin natura ei, deasupra lor. De aceea n-o are niciun rol incorporat —
   * vezi `BUILTIN_CAPS`, unde motivul e scris pe larg.
   */
  manageCompanies: boolean;
  /**
   * Vede CONSOLIDAREA grupului: cifrele tuturor firmelor din grup pe un ecran, fără să aibă
   * acces în fiecare firmă în parte. Se oprește la CITIRE — nu deschide nicio scriere.
   */
  viewGroup: boolean;
};

export type PermissionSet = {
  /** Cheia rolului: una din `COMPANY_ROLES` la cele incorporate, altfel cheia rolului definit. */
  key: string;
  name: string;
  builtin: boolean;
  /** modul → nivel. Modulul absent = NONE (allow-list: un modul nou nu se deschide singur). */
  modules: Readonly<Record<string, PermLevel>>;
  /**
   * Dreptul de ȘTERGERE pe modul, separat de `WRITE`. `null`/absent = ca înainte: cine
   * modifică poate și șterge. Cu el, „poate corecta o factură, dar nu o poate storna”
   * devine exprimabil fără să inventăm module.
   */
  moduleDelete?: Readonly<Record<string, boolean | null>>;
  /**
   * SUPRASCRIERILE pe arborele de acces — pagini (`page:/crm`) și câmpuri
   * (`field:Customer.creditLimit`). Rezolvarea (moștenire + suprascriere) e în
   * `src/lib/access.ts`, sursa unică; aici stau doar datele.
   */
  nodes?: Readonly<Record<string, { read?: boolean | null; write?: boolean | null; remove?: boolean | null }>>;
  /** Excepții per acțiune (`ActionDef.key` → permis/refuzat). Bat dreptul pe modul. */
  actions: Readonly<Record<string, boolean>>;
  caps: Caps;
  /** Intrări de meniu ascunse explicit. Prezentare, nu gardă. */
  navHidden: readonly string[];
};

/** Capabilitățile rolurilor incorporate — exact gărzile de mai jos, scrise ca date. */
const BUILTIN_CAPS: Record<CompanyRole, Caps> = {
  ADMIN:    { admin: true,  approveTime: true,  viewOthersTimesheet: true,  manageHr: true,  seeHrSensitive: true,  approveLeave: true,  seeAllRecords: true,  approvePayments: true,  approveDeals: true,  manageCompanies: false, viewGroup: false },
  MANAGER:  { admin: false, approveTime: true,  viewOthersTimesheet: true,  manageHr: false, seeHrSensitive: false, approveLeave: true,  seeAllRecords: true,  approvePayments: true,  approveDeals: true,  manageCompanies: false, viewGroup: false },
  HR:       { admin: false, approveTime: false, viewOthersTimesheet: true,  manageHr: true,  seeHrSensitive: true,  approveLeave: true,  seeAllRecords: false, approvePayments: false, approveDeals: false, manageCompanies: false, viewGroup: false },
  OPERATOR: { admin: false, approveTime: false, viewOthersTimesheet: false, manageHr: false, seeHrSensitive: false, approveLeave: false, seeAllRecords: false, approvePayments: false, approveDeals: false, manageCompanies: false, viewGroup: false },
};

/**
 * Setul de permisiuni al unui rol incorporat, DERIVAT din `ROLE_POLICY` — nu rescris.
 * Dacă politica unui rol se schimbă sus, setul îl urmează automat; două liste paralele
 * ar fi divergent tăcut, iar divergența ar fi chiar o gaură de acces.
 */
export function builtinPermissions(role: CompanyRole, modules: readonly string[]): PermissionSet {
  const map: Record<string, PermLevel> = {};
  for (const m of modules) {
    map[m] = policyAllows(role, m, true) ? "WRITE" : policyAllows(role, m, false) ? "READ" : "NONE";
  }
  return {
    key: role, name: ROLE_LABEL[role], builtin: true,
    /* Rolurile INCORPORATE n-au suprascrieri pe arbore, deliberat: ele sunt plasa de
       siguranță la care se întoarce o firmă care și-a încurcat rolurile proprii. Fin se
       taie doar pe rolurile compuse. `moduleDelete` gol = ștergerea urmează `WRITE`. */
    modules: map, moduleDelete: {}, nodes: {}, actions: {}, caps: BUILTIN_CAPS[role], navHidden: [],
  };
}

const RANK: Record<PermLevel, number> = { NONE: 0, VIEW: 1, READ: 2, WRITE: 3 };

/**
 * UNIREA a două seturi — cazul „același om, aceeași firmă, două căi de acces": rol direct
 * dat de administratorul firmei + rol de grup dat de operatorul platformei.
 *
 * Asta e ce promitea E1 că înlocuiește rangul. Cu rolurile ca date, unirea chiar POATE fi
 * reprezentată: nivelul mai mare pe fiecare modul, SAU logic pe capabilități. Cu roluri
 * opace (doar un nume), unirea unui HR cu un OPERATOR n-ar fi putut fi exprimată decât
 * ridicând omul la ADMIN — adică exact escaladarea tăcută de evitat.
 *
 * Semantica e „omul deține AMBELE granturi", fiindcă amândouă i-au fost date explicit, de
 * doi oameni diferiți, cu intenții diferite. La excepțiile pe acțiuni, refuzul explicit
 * dintr-un set BATE permisiunea din celălalt: o interdicție punctuală („nu poate storna")
 * e o decizie deliberată, iar un al doilea rol n-are voie s-o ridice din greșeală.
 */
export function mergePermissions(a: PermissionSet, b: PermissionSet): PermissionSet {
  const modules: Record<string, PermLevel> = { ...a.modules };
  for (const [m, lvl] of Object.entries(b.modules)) {
    if (RANK[lvl] > RANK[modules[m] ?? "NONE"]) modules[m] = lvl;
  }
  const actions: Record<string, boolean> = { ...a.actions };
  for (const [k, allow] of Object.entries(b.actions)) {
    actions[k] = k in actions ? actions[k] && allow : allow;
  }
  const caps = {} as Caps;
  for (const k of Object.keys(a.caps) as (keyof Caps)[]) caps[k] = a.caps[k] || b.caps[k];
  /* Ștergerea pe modul și suprascrierile de pe arbore se unesc ca DREPTURI, nu ca refuzuri:
     `true` dintr-un set câștigă, iar `null` (moștenește) nu doboară o decizie luată în
     celălalt. Același raționament ca la nivelul pe modul de mai sus — omul deține AMBELE
     granturi. Refuzul explicit rămâne refuz doar dacă niciun set nu acordă nimic acolo. */
  const moduleDelete: Record<string, boolean | null> = { ...(a.moduleDelete ?? {}) };
  for (const [m, v] of Object.entries(b.moduleDelete ?? {})) {
    moduleDelete[m] = moduleDelete[m] === true || v === true ? true : (moduleDelete[m] ?? v);
  }
  const nodes: Record<string, { read?: boolean | null; write?: boolean | null; remove?: boolean | null }> = { ...(a.nodes ?? {}) };
  for (const [n, ov] of Object.entries(b.nodes ?? {})) {
    const cur = nodes[n];
    if (!cur) { nodes[n] = ov; continue; }
    const pick = (x: boolean | null | undefined, y: boolean | null | undefined) =>
      x === true || y === true ? true : (x ?? y ?? null);
    nodes[n] = { read: pick(cur.read, ov.read), write: pick(cur.write, ov.write), remove: pick(cur.remove, ov.remove) };
  }
  return {
    key: a.key === b.key ? a.key : `${a.key}+${b.key}`,
    name: a.name === b.name ? a.name : `${a.name} + ${b.name}`,
    builtin: a.builtin && b.builtin,
    modules, moduleDelete, nodes, actions, caps,
    // ascuns din meniu doar dacă AMBELE roluri îl ascund — altfel i-am lua o pagină la care are drept
    navHidden: a.navHidden.filter((h) => b.navHidden.includes(h)),
  };
}

/**
 * Setul SUPERADMINULUI: toate modulele la WRITE, toate capabilitățile, în orice firmă.
 *
 * Nu e un rol de firmă și nu se poate compune din ecranul de Roluri — vine din `User.superAdmin`,
 * pus doar de pe mașină. De aceea nici nu apare în `COMPANY_ROLES`: n-are ce căuta într-o listă
 * din care un administrator de firmă poate alege.
 *
 * Derivat din `ACTION_MODULES`, nu scris de mână: un modul nou i se deschide singur, altfel
 * superadminul ar fi fost singurul care rămâne pe dinafară la fiecare modul adăugat.
 */
let superAdminCache: PermissionSet | null = null;
export function superAdminSet(): PermissionSet {
  if (!superAdminCache) {
    const modules: Record<string, PermLevel> = {};
    for (const m of ACTION_MODULES) modules[m] = "WRITE";
    superAdminCache = {
      key: "superadmin", name: "Superadmin", builtin: true,
      modules, moduleDelete: {}, nodes: {}, actions: {},
      caps: {
        admin: true, approveTime: true, viewOthersTimesheet: true, manageHr: true,
        seeHrSensitive: true, approveLeave: true, seeAllRecords: true, approvePayments: true,
        approveDeals: true, manageCompanies: true, viewGroup: true,
      },
      navHidden: [],
    };
  }
  return superAdminCache;
}

/** Setul unui rol incorporat, memoizat pe proces (e pur, derivat din constantele de sus). */
const builtinCache = new Map<CompanyRole, PermissionSet>();
export function builtinSet(role: CompanyRole): PermissionSet {
  let s = builtinCache.get(role);
  if (!s) { s = builtinPermissions(role, ACTION_MODULES); builtinCache.set(role, s); }
  return s;
}

/**
 * Cine cere ceva: rolul (pentru planurile SYSTEM/PLATFORM, care nu sunt roluri de firmă) plus
 * setul lui de permisiuni, când e cunoscut.
 */
export type Caller = {
  role?: string;
  perms?: PermissionSet;
  /**
   * Acțiunea-PĂRINTE, când cererea vine dintr-un orchestrator (`retail.sell → invoice.create`).
   * Dreptul a fost deja verificat pe părinte; sub-acțiunea nu se mai judecă pe modulul ei.
   */
  viaAction?: string;
};

/**
 * Permisiunile apelantului; în lipsa lor, setul rolului INCORPORAT.
 *
 * Rezerva nu e o portiță: pe acolo intră apelanții fără sesiune (cheie API, apeluri interne,
 * filtrul de unelte al agenților), iar ea produce EXACT politica de dinainte de rolurile
 * configurabile. Un rol necunoscut nu produce niciun set — deci cade pe refuz.
 */
export const permsOf = (c: Caller): PermissionSet | undefined =>
  c.perms ?? (isCompanyRole(c.role) ? builtinSet(c.role) : undefined);

/**
 * Capabilitățile apelantului, pentru gărzile punctuale. `SYSTEM` = apelant server de
 * încredere (automatizări, cron, vitrina publică): are tot, ca înainte.
 */
export const capsOf = (c: Caller): { caps: Caps } | undefined =>
  c.role === "SYSTEM" ? { caps: SYSTEM_CAPS } : permsOf(c);

/** Are setul dreptul cerut pe modul? Modulul necunoscut = NONE = refuz. */
export function permAllows(p: PermissionSet | undefined, module: string, mutates: boolean): boolean {
  const level = p?.modules[module] ?? "NONE";
  /* ⚠ Citirea cere READ explicit: `VIEW` NU deschide modulul — doar căutările din documente
     (`permAllowsLookup`). O comparație „!== NONE” ar fi lăsat VIEW să vadă tot. */
  return mutates ? level === "WRITE" : level === "READ" || level === "WRITE";
}

/** Poate CĂUTA/ALEGE înregistrări din modul dintr-un document (acțiune `lookup`)? Orice nivel peste NONE. */
export function permAllowsLookup(p: PermissionSet | undefined, module: string): boolean {
  return (p?.modules[module] ?? "NONE") !== "NONE";
}

/**
 * Poate ȘTERGE în modul? Nedecis (`null`) = ca înainte: `WRITE` includea ștergerea — deci
 * niciun rol existent nu-și schimbă puterea. Decizia explicită, în oricare sens, câștigă.
 *
 * Rezolvarea completă pe arbore (pagină, câmp) e în `src/lib/access.ts`; asta e rădăcina ei.
 */
export function permAllowsDelete(p: PermissionSet | undefined, module: string): boolean {
  const explicit = p?.moduleDelete?.[module];
  if (explicit === true || explicit === false) return explicit;
  return permAllows(p, module, true);
}

/**
 * Are voie apelantul la ACȚIUNEA asta? Excepția punctuală bate dreptul pe modul — în ambele
 * sensuri, ca „poate factura, dar nu poate storna" să fie exprimabil fără să inventăm module.
 */
export function permAllowsAction(p: PermissionSet | undefined, key: string, module: string, mutates: boolean): boolean {
  if (!p) return false;
  const exception = p.actions[key];
  if (exception !== undefined) return exception;
  return permAllows(p, module, mutates);
}

/**
 * Cât de puternic e un rol, când același om ajunge la aceeași firmă pe DOUĂ căi:
 * direct (`Membership`, dat de administratorul firmei) și prin grup (`GroupMembership`,
 * dat de operatorul platformei). Se aplică unul singur, deci regula stă explicit aici.
 *
 * De ce rang și nu „unirea drepturilor": politica reală nu e o ierarhie. HR și OPERATOR
 * sunt INCOMPARABILE — operatorul are mai multe module, dar HR-ul are salariile și CNP-ul
 * (`canSeeHrSensitive`), pe care politica pe module nici măcar nu le descrie. O „unire"
 * corectă a doi asemenea roluri n-ar putea fi reprezentată decât ridicând omul la ADMIN,
 * adică exact escaladarea tăcută de care ne ferim.
 *
 * Reguli, în ordine:
 *   1. rangul mai mare câștigă — un CEO cu rol de grup ADMIN nu poate fi coborât de un rol
 *      local de operator; ăsta e chiar rostul accesului de grup;
 *   2. la rang egal câștigă rolul DIRECT — decizia luată la firmă e cea mai informată
 *      (MANAGER local vs. HR de grup rămâne MANAGER; HR local vs. MANAGER de grup rămâne HR).
 *
 * ⚠ E2 (RBAC pe permisiuni) înlocuiește rangul cu unirea reală a seturilor de permisiuni.
 * Până atunci ăsta e SINGURUL loc care decide — nu duplica regula în apelanți.
 */
export const ROLE_RANK: Record<CompanyRole, number> = { ADMIN: 3, MANAGER: 2, HR: 2, OPERATOR: 1 };

/** Rolul care se aplică efectiv într-o firmă. `direct` câștigă la rang egal. */
export function strongerRole(direct: CompanyRole, fromGroup: CompanyRole): CompanyRole {
  return ROLE_RANK[fromGroup] > ROLE_RANK[direct] ? fromGroup : direct;
}

/* ── Gărzi punctuale, folosite și pe server, și în UI ──────────────────────────
   Primesc CAPABILITATEA, nu numele rolului. Apelanții server de încredere (SYSTEM) trec
   prin `SYSTEM_CAPS`; apelantul fără nimic cade pe refuz, ca înainte.

   ⚠ Nu reintroduce comparații pe numele rolului. Un rol compus de client („Contabil-șef")
   n-are cum să fie „ADMIN" sau „HR", deci o astfel de gardă i-ar refuza pe veci un drept
   pe care administratorul firmei i l-a acordat explicit — și l-ar refuza tăcut. */

/** Cine are ce, ca sursă unică pentru gărzi: un apelant fără capabilități n-are niciuna. */
export type CapHolder = { caps: Caps } | undefined | null;

/** Apelantul server de încredere (SYSTEM): automatizări, cron, agenți interni. Are tot. */
export const SYSTEM_CAPS: Caps = {
  admin: true, approveTime: true, viewOthersTimesheet: true,
  manageHr: true, seeHrSensitive: true, approveLeave: true, seeAllRecords: true, approvePayments: true,
  approveDeals: true,
  /* `manageCompanies` rămâne true AICI, și doar aici: `scripts/provision-company.ts` rulează pe
     mașină cu rolul SYSTEM și trebuie să funcționeze mai departe. SYSTEM nu se poate obține
     dintr-o cerere HTTP — nici cu sesiune, nici cu cheie API. */
  manageCompanies: true,

  /* Apelantul de sistem vede tot, inclusiv consolidarea grupului. */
  viewGroup: true,
};

const cap = (h: CapHolder, k: keyof Caps): boolean => Boolean(h?.caps?.[k]);

/** Aprobă pontajul de proiect. Act de management, nu de personal. */
export const canApproveTime = (h: CapHolder): boolean => cap(h, "approveTime");

/** Vede pontajul altor oameni (nu doar al lui). */
export const canViewOthersTimesheet = (h: CapHolder): boolean => cap(h, "viewOthersTimesheet");

/** Vede și modifică dosare de personal, contracte, salarii, CNP. */
export const canManageHr = (h: CapHolder): boolean => cap(h, "manageHr");

/** Poate administra firmele din grup (creare, acces, roluri peste granița de firmă). */
export const canManageCompanies = (h: CapHolder): boolean => cap(h, "manageCompanies");
export const canViewGroup = (h: CapHolder): boolean => cap(h, "viewGroup");

/** Vede date sensibile de personal (salariu, CNP întreg). Mai strict decât `canManageHr`. */
export const canSeeHrSensitive = (h: CapHolder): boolean => cap(h, "seeHrSensitive");

/** Aprobă cereri de concediu — și șeful direct, nu doar HR-ul. */
export const canApproveLeave = (h: CapHolder): boolean => cap(h, "approveLeave");

/** Vede înregistrările CRM ale altora când firma a restrâns vizibilitatea la proprietar. */
export const canSeeAllRecords = (h: CapHolder): boolean => cap(h, "seeAllRecords");

/** Aprobă facturi de furnizor înainte de plată (pragul din `PaymentApprovalRule`). */
export const canApprovePayments = (h: CapHolder): boolean => cap(h, "approvePayments");

/** Aprobă deal-uri și oferte peste pragurile din `ApprovalRule` (discount/valoare). */
export const canApproveDeals = (h: CapHolder): boolean => cap(h, "approveDeals");

/** Poate rula acțiunile marcate `adminOnly` (administrare utilizatori, integrări, roluri). */
export const canAdminister = (h: CapHolder): boolean => cap(h, "admin");
